import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';
import { Button, Card, Chip, Dialog, EmptyState, Skeleton, toast } from '@/ui';
import { maskEmail, maskPhone, revealOrMask } from '@/lib/admin-mask';
import { fetchPlatformOrganization, setOrganizationStatus } from './api';
import { adminKeys } from './query-keys';
import { invalidateAfterOrganizationMutation } from './invalidation';
import { MEMBER_TYPE_LABEL_KEYS } from './labels';
import { adminErrorMessage } from './error-message';
import { formatCount, formatDateTime } from './format';
import { OrganizationAccountsSection } from './OrganizationAccountsSection';
import { ProvisionMemberDrawer } from './ProvisionMemberDrawer';
import styles from './Admin.module.css';

/**
 * ISLETME DETAYI (`/yonetim/isletmeler/:organizationId`) — menude YOK.
 *
 * ========================== PASIFLESTIRMENIN BEDELI ========================
 * `POST /admin/v1/organizations/status` yalniz bir bayrak cevirmiyor: ETKIN
 * HER UYENIN butun jetonlarini iptal ediyor. Yani islem, o an ekran basinda
 * calisan insanlari aninda disari atar.
 *
 * Bu yuzden onay diyalogu isletmeyi ADIYLA ve KAC KISININ oturumunun
 * dusecegini SAYIYLA soyler; sunucu islemden sonra gercek sayiyi donduruyor ve
 * bildirim onu gosteriyor (onceden bilinen sayi tahmin, sonrasi olcum).
 */

export function PlatformOrganizationDetailPage() {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const organizationId = params.organizationId ?? '';
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [revealContact, setRevealContact] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);

  const query = useQuery({
    queryKey: adminKeys.organizationDetail(organizationId),
    queryFn: () => fetchPlatformOrganization(organizationId),
    enabled: organizationId !== '',
  });

  const status = useMutation({
    mutationFn: setOrganizationStatus,
    onSuccess: async (result, variables) => {
      toast({
        message: variables.isActive
          ? t('admin.organizations.activated')
          : t('admin.organizations.deactivated', {
              n: formatCount(result.revokedMemberCount),
            }),
        tone: 'success',
      });
      await invalidateAfterOrganizationMutation(queryClient);
    },
    onError: (error: unknown) => {
      toast({ message: adminErrorMessage(error, t), tone: 'danger' });
    },
  });

  if (query.isPending) {
    return (
      <Card padding="lg">
        <Skeleton shape="block" height="320px" />
      </Card>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Card padding="lg">
        <EmptyState
          tone="error"
          icon={<ShieldAlert size={28} aria-hidden="true" />}
          title={t('admin.organizations.notFoundTitle')}
          description={adminErrorMessage(query.error, t)}
          action={
            <Button
              tone="neutral"
              onClick={() => {
                void query.refetch();
              }}
            >
              {t('common.retry')}
            </Button>
          }
        />
      </Card>
    );
  }

  const { organization, members } = query.data;
  const activeMembers = members.filter((member) => member.isActive).length;

  return (
    <div className={styles.page}>
      <Card padding="md">
        <div className={styles.pageHead}>
          <div className={styles.rowMain}>
            <h1 className={styles.title}>{organization.organizationName}</h1>
            <span className={styles.muted}>
              {revealOrMask(organization.organizationEmail, revealContact, maskEmail)} ·{' '}
              {revealOrMask(organization.organizationPhone, revealContact, maskPhone)}
            </span>
          </div>
          <div className={styles.badges}>
            <Chip tone={organization.isActive ? 'success' : 'danger'}>
              {organization.isActive ? t('admin.status.active') : t('admin.status.inactive')}
            </Chip>
          </div>
        </div>
        <label className={styles.revealToggle}>
          <input
            type="checkbox"
            checked={revealContact}
            onChange={(event) => {
              setRevealContact(event.target.checked);
            }}
          />
          {t('admin.revealContact')}
        </label>
      </Card>

      <Card padding="md">
        <h2 className={styles.sectionTitle}>{t('admin.organizations.profileSection')}</h2>
        <div className={styles.detailGrid}>
          <Field label={t('org.profile.city')} value={organization.organizationCity} />
          <Field label={t('org.profile.district')} value={organization.organizationDistrict} />
          <Field label={t('org.profile.chamber')} value={organization.businessChamber} />
          <Field label={t('org.profile.chamberNo')} value={organization.businessNumber} />
          <Field
            label={t('admin.organizations.createdAtLabel')}
            value={formatDateTime(organization.createdAt)}
          />
          <Field
            label={t('admin.organizations.memberCountLabel')}
            value={`${formatCount(organization.activeMemberCount)} / ${formatCount(
              organization.memberCount,
            )}`}
          />
        </div>
      </Card>

      <Card padding="md">
        <div className={styles.pageHead}>
          <h2 className={styles.sectionTitle}>{t('admin.organizations.membersSection')}</h2>
          <div className={styles.filterActions}>
            {/* Platform yoneticisi HERHANGI bir isletmeye uye ekleyebilir; isletme
                yoneticisinin kendi ekibine kullandigi akisin platform kapsamli ikizi. */}
            <Button
              size="sm"
              onClick={() => {
                setAddMemberOpen(true);
              }}
            >
              {t('admin.organizations.addMemberAction')}
            </Button>
            <Button
              tone="ghost"
              size="sm"
              onClick={() => {
                // Kimlik ROTA DURUMUNDA tasiniyor, adres cubugunda degil.
                void navigate('/yonetim', { state: { organizationId: organization.id } });
              }}
            >
              {t('admin.organizations.viewAsUsers')}
            </Button>
            <Button
              tone="ghost"
              size="sm"
              onClick={() => {
                void navigate('/yonetim/gecmis', {
                  state: { organizationId: organization.id },
                });
              }}
            >
              {t('admin.organizations.viewAudit')}
            </Button>
          </div>
        </div>

        {members.length === 0 ? (
          <p className={styles.muted}>{t('org.members.empty')}</p>
        ) : (
          <div className={styles.rowList}>
            {members.map((member) => (
              <div key={member.id} className={styles.row}>
                <div className={styles.rowMain}>
                  <span className={styles.rowName}>{member.userFullName}</span>
                  <span className={styles.muted}>
                    {revealOrMask(member.userEmail, revealContact, maskEmail)}
                  </span>
                </div>
                <div className={styles.badges}>
                  {member.memberType === null ? null : (
                    <Chip tone="neutral">
                      {t(MEMBER_TYPE_LABEL_KEYS[member.memberType] ?? '')}
                    </Chip>
                  )}
                  <Chip tone={member.isActive ? 'success' : 'danger'}>
                    {member.isActive ? t('admin.status.active') : t('admin.status.inactive')}
                  </Chip>
                  {/* Kullanici kimligi (appUserId) — uyelik kimligi DEGIL. */}
                  <Link
                    to={`/yonetim/kullanicilar/${member.appUserId}`}
                    className={styles.linkButton}
                  >
                    {t('admin.users.openDetail')}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* IBAN'lar platform yonetiminde (2026-08-30): isletme tarafi salt okunur, tanimlama
          burada. Ayri bir menu ogesi degil cunku hesap ORGANIZASYONA ait bir kaynak. */}
      <OrganizationAccountsSection organizationId={organization.id} />

      <ProvisionMemberDrawer
        open={addMemberOpen}
        onClose={() => {
          setAddMemberOpen(false);
        }}
        organizationId={organization.id}
        organizationName={organization.organizationName}
      />

      <Card padding="md">
        <h2 className={styles.sectionTitle}>{t('admin.organizations.actionsSection')}</h2>
        <p className={styles.notice}>{t('admin.organizations.statusNotice')}</p>
        <div className={styles.actionRow}>
          <Button
            tone={organization.isActive ? 'danger' : 'primary'}
            disabled={status.isPending}
            onClick={() => {
              setConfirmOpen(true);
            }}
          >
            {organization.isActive
              ? t('admin.organizations.deactivateAction')
              : t('admin.organizations.activateAction')}
          </Button>
        </div>
      </Card>

      <Dialog
        open={confirmOpen}
        tone={organization.isActive ? 'danger' : 'primary'}
        title={
          organization.isActive
            ? t('admin.organizations.deactivateTitle')
            : t('admin.organizations.activateTitle')
        }
        description={
          organization.isActive
            ? t('admin.organizations.deactivateDescription', {
                name: organization.organizationName,
                n: activeMembers,
              })
            : t('admin.organizations.activateDescription', {
                name: organization.organizationName,
              })
        }
        confirmLabel={
          organization.isActive
            ? t('admin.organizations.deactivateAction')
            : t('admin.organizations.activateAction')
        }
        cancelLabel={t('common.cancel')}
        busy={status.isPending}
        onCancel={() => {
          setConfirmOpen(false);
        }}
        onConfirm={() => {
          setConfirmOpen(false);
          status.mutate({ organizationId: organization.id, isActive: !organization.isActive });
        }}
      />
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.detailItem}>
      <span className={styles.detailLabel}>{label}</span>
      <span className={styles.detailValue}>{value === '' ? '—' : value}</span>
    </div>
  );
}
